import { useFlags } from "@unleash/proxy-client-react";

import { useGetUserDetailsQuery } from "@store/services/user";
import type { IShopifyProduct } from "@types";
import { productTypes } from "@shared/constants/productTypes";
import { type TProductType } from "@shared/types/product.types";
import { purchaseTypes } from "@shared/constants/purchaseTypes";
import { type TPurchaseType } from "@shared/types/purchase.types";

type TUserType = "user" | "admin";

interface IDisabledProduct {
  product: TProductType;
  variants: TPurchaseType[];
  /** Unleash kill-switch: rule active while the flag is on or omitted; an absent flag disables it. */
  featureFlag?: string;
  userTypes: TUserType[];
}

/** Variants hidden per user type, gated by an optional Unleash flag. */
const disabledProducts: IDisabledProduct[] = [
  {
    product: productTypes.prenup,
    variants: [purchaseTypes.digitalDocumentBasis],
    featureFlag: "disablePrenupBasis",
    userTypes: ["user"],
  },
];

/** Strips disabled purchase types from each product's variants. */
export const filterDisabledVariants = (products: IShopifyProduct[], rules: IDisabledProduct[]): IShopifyProduct[] =>
  products.map((product) => {
    const disabledPurchaseTypes = rules
      .filter((rule) => rule.product === product.productType)
      .flatMap((rule) => rule.variants);
    if (disabledPurchaseTypes.length === 0) {
      return product;
    }
    return {
      ...product,
      variants: product.variants.filter(
        (variant) => variant.purchaseType == null || !disabledPurchaseTypes.includes(variant.purchaseType),
      ),
    };
  });

/** Strips disabled variants from a query result's `data` (no-op when empty or no rules apply). */
export const withDisabledVariantsFiltered = <T extends { data?: IShopifyProduct[] }>(
  result: T,
  rules: IDisabledProduct[],
): T => {
  if (result.data == null || rules.length === 0) {
    return result;
  }
  return { ...result, data: filterDisabledVariants(result.data, rules) };
};

/** Disabled-product rules applying to the current user. */
export const useActiveDisabledRules = (): IDisabledProduct[] => {
  const { data: userDetailsData } = useGetUserDetailsQuery();
  const flags = useFlags();

  const isAdmin = userDetailsData?.impersonated === true && userDetailsData?.authenticated !== false;
  const userType: TUserType = isAdmin ? "admin" : "user";
  const enabledFlags = new Set(flags.filter((flag) => flag.enabled).map((flag) => flag.name));
  const isRuleFlagActive = (featureFlag?: string): boolean => featureFlag == null || enabledFlags.has(featureFlag);

  return disabledProducts.filter((rule) => rule.userTypes.includes(userType) && isRuleFlagActive(rule.featureFlag));
};

/** Whether a variant is disabled for the current user. Useful for URL-entered flows. */
export const useIsVariantDisabled = () => {
  const activeRules = useActiveDisabledRules();

  return (productType: TProductType, purchaseType: TPurchaseType): boolean =>
    activeRules.some((rule) => rule.product === productType && rule.variants.includes(purchaseType));
};
